import {
  Button,
  ModalProps,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { FC, useMemo, useRef, useState } from 'react';

import { GenericModal } from '../../components/modals/generic-modal/generic-modal';

/**
 * An agent's prompt in Agent settings: rendered as markdown in a short
 * preview in the panel, and in a large modal that switches between the
 * rendered prompt and its text. Done keeps the edit in the panel's draft
 * (the panel's Save writes it); Cancel puts back what was there when it
 * opened.
 */

/** The prompt as sanitized HTML. Its text is the solution builders'. */
const renderMarkdown = (text: string): string =>
  DOMPurify.sanitize(
    marked.parse(text, { async: false, gfm: true, breaks: true }) as string
  );

const PromptMarkdown: FC<{ text: string; className: string }> = ({
  text,
  className,
}) => {
  const html = useMemo(() => renderMarkdown(text), [text]);
  return (
    <div
      className={`c-agent-prompt-md ${className}`}
      // Sanitized above: marked's HTML through DOMPurify.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export const AgentPromptEditor: FC<{
  /** The modal's heading, e.g. "Knowledge Agent prompt". */
  title: string;
  /** One line under it: what the prompt is for. */
  description?: string;
  value: string;
  isViewer: boolean;
  testId: string;
  onChange: (value: string) => void;
}> = ({ title, description, value, isViewer, testId, onChange }) => {
  const modalRef = useRef<ModalProps>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [before, setBefore] = useState(value);
  const lines = value.split('\n').length;
  const show = (edit: boolean) => {
    setBefore(value);
    setEditing(edit && !isViewer);
    setOpen(true);
  };
  const close = (keep: boolean) => {
    if (!keep) onChange(before);
    setOpen(false);
  };
  return (
    <>
      <div className="c-agent-settings__prompt-preview" data-testid={testId}>
        {value ? (
          <PromptMarkdown
            text={value}
            className="c-agent-settings__prompt-clamp"
          />
        ) : (
          <p className="c-agent-settings__none">No prompt yet.</p>
        )}
        <div className="c-agent-settings__prompt-actions">
          <button
            type="button"
            className="c-agent-settings__link"
            onClick={() => show(false)}
          >
            View full prompt · {lines} line{lines === 1 ? '' : 's'}
          </button>
          {!isViewer && (
            <button
              type="button"
              className="c-agent-settings__link"
              data-testid={`${testId}-edit`}
              onClick={() => show(true)}
            >
              Edit
            </button>
          )}
        </div>
      </div>
      <GenericModal
        modalRef={modalRef}
        open={open}
        size="large"
        onClose={() => close(true)}
        test-id={`${testId}-modal`}
      >
        <div slot="header" className="c-agent-settings__prompt-head">
          <div>
            <TextTitle size="small">{title}</TextTitle>
            {description && (
              <TextCaption variant="secondary">{description}</TextCaption>
            )}
          </div>
          {!isViewer && (
            <div
              className="c-agent-settings__prompt-mode"
              role="radiogroup"
              aria-label="Show"
            >
              {(
                [
                  [false, 'Preview'],
                  [true, 'Edit'],
                ] as const
              ).map(([edit, label]) => (
                <button
                  key={label}
                  type="button"
                  role="radio"
                  aria-checked={editing === edit}
                  className={editing === edit ? 'is-on' : ''}
                  onClick={() => setEditing(edit)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {editing ? (
          <textarea
            className="c-agent-settings__prompt c-agent-settings__prompt--modal"
            data-testid={`${testId}-modal-text`}
            aria-label={title}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <PromptMarkdown
            text={value}
            className="c-agent-settings__prompt-full"
          />
        )}
        <div slot="footer" className="c-agent-settings__prompt-foot">
          {isViewer ? (
            <Button size="small" onClick={() => close(true)}>
              Close
            </Button>
          ) : (
            <>
              <Button size="small" variant="ghost" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button size="small" onClick={() => close(true)}>
                Done
              </Button>
            </>
          )}
        </div>
      </GenericModal>
    </>
  );
};
